import { useContent } from "@/hooks/useContent";
import styled from "styled-components";
import { useAppStore, AppStoreState } from "@/stores/appStore";
import { useState, useEffect } from "react";

interface Modifier {
	id: string;
	title: string;
	location: string;
	icon?: React.ReactNode;
}

interface ModifiersProps {
	modifiersArray: string[];
	stateKey: keyof AppStoreState["configurator"];
	stateType: "selectedModifiers";
	defaultVal: Array<{ id: string }>;
}

export const Modifiers = ({ modifiersArray, stateKey, stateType, defaultVal }: ModifiersProps) => {
	const [activeModifiers, setActiveModifiers] = useState(defaultVal);

	const { getModifiers } = useContent();
	const modifiers = getModifiers().filter((modifier) => modifiersArray?.includes(modifier.id));

	const handleModifier = (id: string, location: string) => {
		setActiveModifiers((prev) => {
			const isCurrentlyActive = prev.some((mod) => mod.id === id);

			if (isCurrentlyActive) {
				return prev.filter((mod) => mod.id !== id);
			} else {
				const filteredModifiers = prev.filter((mod) => mod.location !== location);
				return [...filteredModifiers, { id, location }];
			}
		});
	};

	const isDisabled = (modifier: Modifier) => {
		const activeModifierForLocation = activeModifiers.find((active) => active.location === modifier.location);
		return activeModifierForLocation && activeModifierForLocation.id !== modifier.id;
	};

	const isActive = (modifierId: string) => {
		return activeModifiers.some((mod) => mod.id === modifierId);
	};

	const updateState = () => {
		const state = (prevState: AppStoreState) => ({
			...prevState.configurator,
			[stateKey]: {
				...prevState.configurator[stateKey],
				[stateType]: activeModifiers,
			},
		});

		useAppStore.setState((prevState) => ({
			configurator: state(prevState),
		}));
	};

	useEffect(() => {
		updateState();
	}, [activeModifiers]);

	return (
		<ModifierContainer>
			{modifiers.map((modifier) => (
				<li key={modifier.id}>
					<ListItem
						data-disabled={isDisabled(modifier)}
						data-active={isActive(modifier.id)}
						onClick={() => handleModifier(modifier.id, modifier.location)}>
						{modifier.icon && modifier.icon()}
						{modifier.title}
					</ListItem>
				</li>
			))}
		</ModifierContainer>
	);
};

const ModifierContainer = styled.ul`
	display: grid;
	grid-template-columns: repeat(2, minmax(0, 1fr));
	gap: 10px;
	margin: 0;
	padding: 0;
	list-style: none;
`;

const ListItem = styled.div`
	--buttonColor: #7d6a64;

	background-color: #e9e1de;
	padding-block: 0.8rem;
	display: flex;
	column-gap: 10px;
	padding-inline: 16px;
	align-items: center;
	cursor: pointer;
	border-radius: 5px;
	color: var(--buttonColor);
	height: 100%;
	transition: background-color 98ms linear;

	svg {
		fill: var(--buttonColor);
		width: 20px;
	}

	&[data-active="true"] {
		background-color: ${({ theme }) => theme.colors.secondary};
		--buttonColor: white;
	}

	&[data-disabled="true"] {
		background-color: #e9e1de;
		pointer-events: none;
		--buttonColor: #b1b1b1;
	}

	&:hover {
		background-color: ${({ theme }) => theme.colors.secondary};
		--buttonColor: white;
	}
`;
